/**
 * [TD-73 Section 2.3] Lobby ambient music player.
 *
 * Dedicated `<audio loop>` element separate from `auctioneerAudio.js`'s
 * Adam TTS singleton. Coexists in the DOM; no shared state.
 *
 * Exported API:
 *   startAmbient({src, volume}) → Promise<{started, blocked}>
 *   stopAmbient()
 *   duckAmbient(factor=0.5) → number   // ratio of current level, fades
 *   fadeOutAmbient(fadeMs=1500) → Promise<void>
 *   swapTrack({src, volume, fadeMs=400}) → Promise<void>
 *   setAmbientVolume(0..1)
 *   getAmbientVolume() → number
 *   isAmbientPlaying() → boolean
 *   previewTrack(src, durationMs=5000) → Promise<void>  // suspends live
 *                                                       // ambient during sample
 *
 * isReady() gate: defers start when auctioneerAudio's prewarm hasn't
 * completed yet (max 5 retries × 16ms before giving up).
 */

import { isReady } from "./auctioneerAudio";

let _ambient = null;
let _volume = 0.4;
let _suspended = null; // { src, volume } during preview

function _ensureElement() {
  if (_ambient) return _ambient;
  const el = document.createElement("audio");
  el.loop = true;
  el.preload = "auto";
  el.volume = _volume;
  el.setAttribute("data-pgf-lobby-ambient", "true");
  document.body.appendChild(el);
  _ambient = el;
  return el;
}

function _waitForReady(maxRetries = 5) {
  return new Promise((resolve) => {
    let tries = 0;
    const tick = () => {
      if (isReady()) return resolve(true);
      if (++tries >= maxRetries) return resolve(false);
      requestAnimationFrame(tick);
    };
    tick();
  });
}

export async function startAmbient({ src, volume } = {}) {
  if (!src) return { started: false, blocked: false };
  const el = _ensureElement();
  if (typeof volume === "number") {
    _applyVolumeRequest(volume);
  }
  el.volume = _volume;

  // [Gap F.1] Wait for the auctioneerAudio prewarm to finish so we're not
  // racing the iOS Safari unlock. Best-effort retry - if it's still not
  // ready we still attempt play().
  await _waitForReady();

  if (el.src !== src) el.src = src;
  try {
    await el.play();
    return { started: true, blocked: false };
  } catch {
    // Autoplay policy blocked. Caller surfaces the F.a.1 pill.
    return { started: false, blocked: true };
  }
}

export function stopAmbient() {
  if (!_ambient) return;
  try {
    _ambient.pause();
    _ambient.currentTime = 0;
  } catch {
    // ignore
  }
  _ambient.removeAttribute("src");
  _ambient.load();
}

// [BUG-B] Single choke point for every "play at this level" request. While
// the bed is ducked for the reveal/countdown, a request does not un-duck it -
// it becomes the level the duck will hand back to. Without this, the lobby's
// LOBBY-scoped autoplay effect and the volume slider both stomped the duck.
function _applyVolumeRequest(v) {
  const req = Math.max(0, Math.min(1, v));
  if (_preDuckVolume !== null) {
    _preDuckVolume = req;
    return _volume;
  }
  _volume = req;
  return _volume;
}

export function setAmbientVolume(v) {
  _applyVolumeRequest(v);
  if (_ambient) _ambient.volume = _volume;
}

/**
 * [ADAM AUDIO batch 2 item 1] Duck the MUSIC (not Adam's voice) for the
 * order reveal + countdown, remembering the level to come back to.
 * Idempotent: a second duck while already ducked does not overwrite the
 * remembered level.
 *
 * [BUG-B] `factor` is a RATIO of the current level, not an absolute volume.
 * The old absolute reading meant duckAmbient(0.5) against a 0.40 user
 * volume made the music LOUDER, which is why the reveal never sounded
 * ducked. Also fades instead of stepping.
 */
let _preDuckVolume = null;

export function duckAmbient(factor = 0.5) {
  if (_preDuckVolume === null) _preDuckVolume = _volume;
  const target = Math.max(0, Math.min(1, _preDuckVolume * factor));
  _volume = target;
  if (_ambient) _fade(_ambient, _ambient.volume, target, 600);
  return target;
}

/**
 * [BUG-B] End of the sequence: the first pick begins, so the lobby bed
 * fades OUT rather than hard-stopping mid-bar. The remembered pre-duck
 * level is handed back to `_volume` so any later start plays at the
 * manager's chosen volume.
 */
export async function fadeOutAmbient(fadeMs = 1500) {
  const el = _ambient;
  if (el && !el.paused && el.src) {
    await _fade(el, el.volume, 0, fadeMs);
  }
  stopAmbient();
  if (_preDuckVolume !== null) {
    _volume = _preDuckVolume;
    _preDuckVolume = null;
  }
}

export function getAmbientVolume() {
  return _volume;
}

export function isAmbientPlaying() {
  return !!_ambient && !_ambient.paused && _ambient.src !== "";
}

function _fade(el, from, to, durationMs) {
  return new Promise((resolve) => {
    if (durationMs <= 0) {
      el.volume = to;
      return resolve();
    }
    const steps = Math.max(1, Math.floor(durationMs / 25));
    const delta = (to - from) / steps;
    let i = 0;
    el.volume = from;
    const id = setInterval(() => {
      i += 1;
      const next = Math.max(0, Math.min(1, from + delta * i));
      el.volume = next;
      if (i >= steps) {
        clearInterval(id);
        el.volume = to;
        resolve();
      }
    }, durationMs / steps);
  });
}

export async function swapTrack({ src, volume, fadeMs = 400 } = {}) {
  const el = _ensureElement();
  const targetVol = typeof volume === "number"
    ? _applyVolumeRequest(volume)
    : _volume;
  // Fade out current
  if (!el.paused && el.src) {
    await _fade(el, el.volume, 0, fadeMs);
  }
  if (el.src !== src) el.src = src;
  try {
    await el.play();
    await _fade(el, 0, targetVol, fadeMs);
    _volume = targetVol;
  } catch {
    // Best-effort - if play is blocked we silently bail. The autoplay-block
    // pill is the responsibility of the lobby mount-time effect, not swap.
  }
}

export async function previewTrack(src, durationMs = 5000) {
  if (!src) return;
  const el = _ensureElement();
  // Suspend live ambient (remember src + volume).
  if (!el.paused && el.src) {
    _suspended = { src: el.src, volume: _volume };
    await _fade(el, el.volume, 0, 200);
  }
  if (el.src !== src) el.src = src;
  el.volume = _volume;
  try {
    await el.play();
  } catch {
    _suspended = null;
    return;
  }
  await new Promise((r) => setTimeout(r, durationMs));
  await _fade(el, el.volume, 0, 200);
  if (_suspended) {
    el.src = _suspended.src;
    el.volume = _suspended.volume;
    try { await el.play(); } catch { /* ignore */ }
    await _fade(el, 0, _suspended.volume, 300);
    _suspended = null;
  } else {
    stopAmbient();
  }
}

// Test-only reset to clear module state between unit tests.
export function __resetForTests__() {
  _preDuckVolume = null;
  if (_ambient) {
    try { _ambient.pause(); } catch { /* ignore */ }
    if (_ambient.parentNode) _ambient.parentNode.removeChild(_ambient);
  }
  _ambient = null;
  _volume = 0.4;
  _suspended = null;
}
